// The shop floor's work queue.
//
// This screen used to read the legacy `/cases` module and filter it to `assigned|production|qc`.
// That table was never populated by this lab — every case lives in the workflow module — so the
// queue was permanently "No cases in the production queue" while the floor had work waiting. It
// now reads the workflow case list filtered to stages in the PRODUCTION group, and logging writes
// a workflow production record, which is the same record the case workspace's Production tab shows.
//
// Deliberately simpler than the case workspace: someone at a printer wants the queue, the case in
// front of them, and one dialog to say what they did.
import { Fragment, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  WF_PRODUCTION_KINDS,
  formatDate,
  type WfCase,
  type WfProductionKind,
} from '@alignova/shared';
import { PageHeader, Card } from '../../ui/primitives.tsx';
import { SkeletonList } from '../../ui/mobile.tsx';
import { useLiveEvent } from '../../lib/useLive.ts';
import { useCan } from '../../auth/useCan.ts';
import {
  WfConfigProvider,
  useWfConfig,
  wfApi,
  unwrap,
} from '../workflow/workflowApi.ts';
import { AgeBadge, Btn, DueBadge, Empty, Field, Modal, PriorityPill, StagePill, inputCls } from '../workflow/wfUi.tsx';
import { ProductionCounters, ProformaPrintButton, alignerSummary, stageProgress } from '../workflow/WfProforma.tsx';

const KIND_LABELS: Record<string, string> = {
  print_order: 'Print order',
  print_run: 'Print run',
  print_qc: 'Print QC',
  punching: 'Punching',
  touch_up: 'Touch-up',
  finishing: 'Finishing',
  packaging: 'Packaging',
  reprint: 'Reprint',
};
const label = (k: string) => KIND_LABELS[k] ?? k.replace(/_/g, ' ');

export function ManufacturingPage() {
  // The queue needs stage names and colours, and this route sits outside the workflow shell that
  // normally provides them.
  return (
    <WfConfigProvider>
      <Queue />
    </WfConfigProvider>
  );
}

function Queue() {
  const can = useCan();
  const cfg = useWfConfig();
  const [cases, setCases] = useState<WfCase[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [logging, setLogging] = useState<WfCase | null>(null);
  // Rows opened to show the upper / lower counters and the proforma button.
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (id: string) => setOpen((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const load = useCallback(() => {
    wfApi
      .list({ group: 'production', limit: 100 })
      .then((r) => {
        setCases(r.items);
        setError(null);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);
  useEffect(load, [load]);
  useLiveEvent('workflow.changed', load);

  return (
    <div>
      <PageHeader
        title="Manufacturing"
        subtitle="Every case sitting in a production stage - printing, finishing, packaging."
        action={<span className="tabnum text-sm text-muted">{cases?.length ?? 0} in the queue</span>}
      />

      {error && <p className="mb-3 text-sm text-status-urgent-fg">{error}</p>}
      {!cases && <SkeletonList rows={4} />}

      {cases && cases.length === 0 && (
        <Empty>
          Nothing is in production right now. Cases appear here as soon as they reach a printing,
          finishing or packaging stage.
        </Empty>
      )}

      {cases && cases.length > 0 && (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="border-b border-line bg-surface-2">
              <tr>
                {['Case', 'Type', 'Stage', 'Aligners U / L', 'Due', 'Assigned', ''].map((h) => (
                  <th key={h} className="px-3 py-2.5 text-left text-[12px] font-semibold text-muted">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cases.map((c) => { const prog = stageProgress(c); return (
                <Fragment key={c.id}>
                <tr className="border-t border-line transition-colors hover:bg-surface-2/50">
                  <td className="px-3 py-2.5 align-top">
                    <div className="flex items-center gap-2">
                      <Link to={`/workflow/cases/${c.id}`} className="font-semibold text-ink hover:underline">
                        {c.patientName}
                      </Link>
                      <PriorityPill p={c.priority} />
                    </div>
                    <div className="mt-0.5 flex flex-wrap gap-x-1.5 whitespace-nowrap font-mono text-[11px] text-muted">
                      {c.groupId && <span className="font-semibold text-ink">{c.groupId}</span>}
                      <span>{c.caseNumber}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 align-top">
                    {c.caseTypeRaw ?? c.caseTypes.map((t) => cfg.optionLabel('case_type', t)).join(', ')}
                    {alignerSummary(c) ? <div className="tabnum text-[11px] text-muted">{alignerSummary(c)}</div> : null}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5 align-top">
                    <span className="inline-flex items-center gap-1.5">
                      <StagePill stageKey={c.stageKey} small />
                      <AgeBadge c={c} />
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5 align-top">
                    {prog ? (
                      <button type="button" onClick={() => toggle(c.id)} className="text-left" title="Show the counters">
                        <div className="tabnum text-xs">
                          <span className="text-muted">{prog.label}</span> U <b>{prog.upper[0]}</b>/{prog.known ? prog.upper[1] : '?'} · L <b>{prog.lower[0]}</b>/{prog.known ? prog.lower[1] : '?'}
                        </div>
                        <div className={`text-[11px] ${prog.known ? (prog.left > 0 ? 'text-status-warn-fg' : 'text-status-progress-fg') : 'text-muted'}`}>
                          {!prog.known ? 'numbers not set' : prog.left > 0 ? `${prog.left} left` : 'all done ✓'} {open.has(c.id) ? '▴' : '▾'}
                        </div>
                      </button>
                    ) : (
                      <span className="text-xs text-muted">-</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 align-top">
                    <DueBadge c={c} />
                  </td>
                  <td className="px-3 py-2.5 align-top text-xs">{c.assignedToName ?? <span className="text-muted">-</span>}</td>
                  <td className="px-3 py-2.5 text-right align-top">
                    <div className="flex flex-wrap justify-end gap-1.5">
                      <Btn tone="ghost" onClick={() => toggle(c.id)}>{open.has(c.id) ? 'Hide counts' : 'Counts'}</Btn>
                      {can('workflow:update') && (
                        <Btn tone="ghost" onClick={() => setLogging(c)}>
                          Log work
                        </Btn>
                      )}
                    </div>
                  </td>
                </tr>
                {open.has(c.id) && (
                  <tr className="border-t border-line/60 bg-surface-2/40">
                    <td colSpan={7} className="px-3 py-3">
                      <div className="flex flex-wrap items-start gap-4">
                        <div className="min-w-[20rem]"><ProductionCounters c={c} onChanged={load} compact /></div>
                        <div className="text-xs text-muted">
                          <div className="mb-1 font-semibold uppercase tracking-wider">Case proforma</div>
                          <p className="mb-2 max-w-[16rem]">Goes in the box with the aligners at Packaging.</p>
                          <ProformaPrintButton caseId={c.id} />
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              ); })}
            </tbody>
          </table>
        </Card>
      )}

      <LogModal
        c={logging}
        onClose={() => setLogging(null)}
        onDone={() => {
          setLogging(null);
          load();
        }}
      />
    </div>
  );
}

/**
 * What was done, on which case. Writes a workflow production record; ticking "move the case on"
 * asks the server to advance the stage in the same call, so the floor never has to remember to do
 * it in a second screen.
 */
function LogModal({ c, onClose, onDone }: { c: WfCase | null; onClose: () => void; onDone: () => void }) {
  const cfg = useWfConfig();
  const [kind, setKind] = useState<WfProductionKind>('print_run');
  const [notes, setNotes] = useState('');
  const [moveTo, setMoveTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!c) return;
    setKind('print_run');
    setNotes('');
    setMoveTo('');
    setErr(null);
  }, [c]);

  if (!c) return null;

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await wfApi.production(c.id, {
        kind,
        status: 'completed',
        notes: notes.trim() || null,
        moveTo: moveTo || null,
      });
      // A queued (offline) write returns a marker rather than the case; either way the row is done.
      unwrap(r);
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const nextStages = cfg.stages.filter((s) => s.isActive && s.key !== c.stageKey);

  return (
    <Modal
      open
      onClose={onClose}
      title={`Log work - ${c.patientName} · ${c.caseNumber}`}
      footer={
        <>
          <Btn tone="ghost" onClick={onClose}>
            Cancel
          </Btn>
          <Btn tone="accent" disabled={busy} onClick={() => void submit()}>
            {busy ? 'Saving…' : 'Save log'}
          </Btn>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-3">
        {err && <div className="text-sm text-status-urgent-fg">{err}</div>}
        <Field label="Operation">
          <select className={inputCls} value={kind} onChange={(e) => setKind(e.target.value as WfProductionKind)}>
            {WF_PRODUCTION_KINDS.map((k) => (
              <option key={k} value={k}>
                {label(k)}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Notes" hint="Machine, batch, anything the next person needs to know.">
          <textarea className={inputCls} rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
        <Field label="Move the case on" hint="Optional - leave blank to log the work without changing the stage.">
          <select className={inputCls} value={moveTo} onChange={(e) => setMoveTo(e.target.value)}>
            <option value="">Stay in {cfg.stageName(c.stageKey)}</option>
            {nextStages.map((s) => (
              <option key={s.key} value={s.key}>
                {s.name}
              </option>
            ))}
          </select>
        </Field>
        {c.dueDate && (
          <p className="text-xs text-muted">
            Due {formatDate(c.dueDate)}
            {c.isOverdue ? ' - already overdue' : ''}.
          </p>
        )}
      </div>
    </Modal>
  );
}
